import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Flame } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PhotoPicker from "@/components/PhotoPicker";
import { api, formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { enqueueSubmission } from "@/lib/offlineQueue";

const MAX_REASON = 600;

function StreakBadge({ days }) {
  if (!days || days < 1) return null;
  const label = days === 1 ? "day" : "days";
  const tone = days >= 7 ? "bg-[#FF5A00] text-white" : "bg-[#FFCC00] text-[#09090B]";
  return (
    <div
      data-testid="streak-badge"
      className={`mt-6 inline-flex items-center gap-2 border-2 border-[#09090B] px-4 py-2 ws-label ws-hard-shadow ${tone}`}
    >
      <Flame strokeWidth={2.5} className="w-4 h-4" />
      <span>{days}-{label} story streak — keep it going</span>
    </div>
  );
}

export default function Submit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ employee_name: "", employee_id: "", reason: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [streakDays, setStreakDays] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/submissions/streak")
      .then((r) => (cancelled ? null : setStreakDays(r.data?.streak_days || 0)))
      .catch((err) => logger.warn("[submit] streak load failed", err));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
    setError("");
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPreview(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.employee_name.trim() || !form.employee_id.trim()) {
      setError("Please enter your name and employee ID.");
      return;
    }
    if (form.reason.trim().length < 10) {
      setError("Tell us a little more — at least 10 characters.");
      return;
    }
    if (!photo) {
      setError("Please attach a photo of your 'why'.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("employee_name", form.employee_name.trim());
      fd.append("employee_id", form.employee_id.trim());
      fd.append("reason", form.reason.trim());
      fd.append("photo", photo);
      const { data } = await api.post("/submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate(`/thanks/${data.id}`, { state: { submission: data } });
    } catch (err) {
      // Network failure (offline / poor cell signal) → queue and ack.
      // Any HTTP response from the server means we are online; surface
      // the server's validation error to the user instead of queuing.
      const isNetwork = !err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error" || !navigator.onLine);
      if (isNetwork) {
        try {
          await enqueueSubmission({
            employee_name: form.employee_name.trim(),
            employee_id: form.employee_id.trim(),
            reason: form.reason.trim(),
            photo,
          });
          window.dispatchEvent(new Event("ws-queue-changed"));
          toast.success("You're offline — your story will upload as soon as you reconnect.");
          navigate("/wall");
          return;
        } catch (queueErr) {
          setError("Could not save your story locally. Please try again when you have signal.");
          setSubmitting(false);
          return;
        }
      }
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-[#09090B] min-h-screen">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B] bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
          <div className="ws-label text-[#FF5A00]">/ Submission</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
            TELL US WHY<br />YOU WORK SAFE.
          </h1>
          <p className="mt-4 text-[#52525B] text-lg leading-relaxed max-w-xl">
            Two minutes. One photo. Your story joins the Wall and you earn a Safety Token.
          </p>
          <StreakBadge days={streakDays} />
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <form onSubmit={onSubmit} className="space-y-8" data-testid="submission-form">
          <PhotoPicker preview={preview} onSelectFile={handleFile} onClear={clearPhoto} />

          {/* Identity */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="ws-label">Step 02 · Name</label>
              <input
                id="name"
                data-testid="employee-name-input"
                type="text"
                value={form.employee_name}
                onChange={handleField("employee_name")}
                placeholder="Maria Rivera"
                className="mt-3 w-full border-2 border-[#09090B] bg-white px-4 py-3 focus:border-[#FF5A00] outline-none font-medium"
                maxLength={120}
              />
            </div>
            <div>
              <label htmlFor="empid" className="ws-label">Step 03 · Employee ID</label>
              <input
                id="empid"
                data-testid="employee-id-input"
                type="text"
                value={form.employee_id}
                onChange={handleField("employee_id")}
                placeholder="EMP-1042"
                className="mt-3 w-full border-2 border-[#09090B] bg-white px-4 py-3 focus:border-[#FF5A00] outline-none font-medium"
                maxLength={60}
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="reason" className="ws-label">Step 04 · Why I work safe</label>
              <span className="text-xs text-[#52525B]">
                {form.reason.length}/{MAX_REASON}
              </span>
            </div>
            <textarea
              id="reason"
              data-testid="reason-textarea"
              value={form.reason}
              onChange={handleField("reason")}
              rows={5}
              maxLength={MAX_REASON}
              placeholder="Because my daughter is starting kindergarten in the fall…"
              className="mt-3 w-full border-2 border-[#09090B] bg-white px-4 py-3 focus:border-[#FF5A00] outline-none font-medium resize-y"
            />
          </div>

          {error && (
            <div
              data-testid="error-banner"
              className="border-2 border-[#DC2626] bg-[#FEE2E2] text-[#DC2626] px-4 py-3 ws-label"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t-2 border-dashed border-[#09090B]">
            <div className="flex items-center gap-2 text-sm text-[#52525B]">
              <CheckCircle2 strokeWidth={2.5} className="w-5 h-5 text-[#16A34A]" />
              You&apos;ll receive your Safety Token on the next screen.
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-story-btn"
              className="inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65100] text-white font-bold px-8 py-4 ws-label border-2 border-[#09090B] ws-hard-shadow disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit & Earn Token"}
              {!submitting && <ArrowRight strokeWidth={2.5} className="w-5 h-5" />}
            </button>
          </div>
        </form>
      </section>

      <SiteFooter />
    </div>
  );
}
